import React, { useMemo } from 'react';
import { Box, Divider, Grid, makeStyles } from '@material-ui/core';

import { CustomizedTypography } from '@components/Customized';
import { colorSet } from '@components/Provider';
import {
  CapaQuotationDetailedQuotation,
  CapaRfqQuotationType
} from '@generated/graphql';
import { formatCurrency, formatDate } from '@utils/formatter';
import QuotatonViewItemsTable from './QuotationViewItemsTable';
import QuotationViewPcbItemsTable from './QuotationViewPcbItemsTable';

export interface Props extends Partial<CapaQuotationDetailedQuotation> {
  matches?: boolean;
  haveConsulted: boolean;
  previewMode?: boolean;
}

const ReceivedQuotationNormalForm = (props?: Props) => {
  const classes = useStyles();
  const haveFabricationItems = !!(props?.pcbFabricationItems ?? []).length;
  const haveAssemblyItems = !!(props?.pcbAssemblyItems ?? []).length;

  const supplyCost = useMemo(() => {
    if (props?.type === CapaRfqQuotationType.Simple) return props.quotation;

    const itemsPrice = (props?.items ?? []).reduce(
      (acc, cur) => acc + (cur.cost ? Number(cur.cost) : 0),
      0
    );
    const fabricationItmesPrice = (props?.pcbFabricationItems ?? []).reduce(
      (acc, cur) => acc + (cur.cost ? Number(cur.cost) : 0),
      0
    );
    const assemblyItemsPrice = (props?.pcbAssemblyItems ?? []).reduce(
      (acc, cur) => acc + (cur.cost ? Number(cur.cost) : 0),
      0
    );

    return itemsPrice + fabricationItmesPrice + assemblyItemsPrice;
  }, [props]);

  const vat = supplyCost ? Math.floor(supplyCost * 0.1) : 0;
  const deliveryCost = props?.deliveryCost ? props.deliveryCost : 0;
  const total = deliveryCost + vat + (supplyCost ? supplyCost : 0);

  return (
    <div>
      {(props?.items ?? []).length > 0 && (
        <QuotatonViewItemsTable
          quotationId={props?.id}
          projectName={props?.projectName || undefined}
          matches={props?.matches ?? false}
          items={props?.items ?? []}
          // 일반 도면 견적서라 noDrawing은 항상 false
          noDrawing={false}
        />
      )}
      {haveFabricationItems && (
        <Box style={{ padding: `16px 0` }}>
          <QuotationViewPcbItemsTable
            quotationId={props?.id}
            shouldRenderDownloadButton={haveFabricationItems}
            projectName={props?.projectName ?? ''}
            items={props?.pcbFabricationItems ?? []}
          />
        </Box>
      )}

      {haveAssemblyItems && (
        <Box>
          <QuotationViewPcbItemsTable
            quotationId={props?.id}
            shouldRenderDownloadButton={
              !haveFabricationItems && haveAssemblyItems
            }
            projectName={props?.projectName ?? ''}
            items={props?.pcbAssemblyItems ?? []}
          />
        </Box>
      )}

      {props?.matches && <Divider className="divider" />}

      <Box>
        <Grid
          container
          wrap="nowrap"
          direction="column"
          className={classes.priceInfo}
        >
          <Grid item xs={12} className="price-item gutter-bottom-8">
            <CustomizedTypography variant="body1" color="textPrimary">
              공급가액
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              fontWeight="500"
              color="textPrimary"
            >
              {formatCurrency(supplyCost ?? 0)}&nbsp;원
            </CustomizedTypography>
          </Grid>
          <Grid item xs={12} className="price-item gutter-bottom-8">
            <CustomizedTypography variant="body1" color="textPrimary">
              부가세 (10%)
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              fontWeight="500"
              color="textPrimary"
            >
              {supplyCost ? formatCurrency(vat) : 0}
              &nbsp;원
            </CustomizedTypography>
          </Grid>
          <Grid item xs={12} className="price-item">
            <CustomizedTypography variant="body1" color="textPrimary">
              배송비
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              fontWeight="500"
              color="textPrimary"
            >
              {formatCurrency(props?.deliveryCost ?? 0)}&nbsp;원
            </CustomizedTypography>
          </Grid>
        </Grid>

        <Divider style={{ background: colorSet.gray1000, margin: `16px 0` }} />

        <Grid
          container
          wrap="nowrap"
          direction="column"
          className={classes.priceInfo}
        >
          <Grid item xs={12} className="price-item">
            <CustomizedTypography
              variant="h4"
              color="textPrimary"
              fontWeight="bold"
            >
              총 견적금액
            </CustomizedTypography>
            <CustomizedTypography
              variant="h4"
              color="primary"
              fontWeight="bold"
            >
              {formatCurrency(total ?? 0)}&nbsp;원
            </CustomizedTypography>
          </Grid>
        </Grid>
      </Box>
      <Divider className="divider" />

      {props?.etc && (
        <>
          <Box className="rfq-explanation">
            <CustomizedTypography
              variant="h4"
              fontWeight="500"
              gutterBottoms={16}
            >
              견적 설명
            </CustomizedTypography>

            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
              className="pre-line"
            >
              {props?.etc}
            </CustomizedTypography>
          </Box>
          <Divider className="divider" />
        </>
      )}
      {isNaN(Number(props?.dueDate)) ||
      !props?.dueDate ||
      Number(props.dueDate) === 0 ? null : (
        <React.Fragment>
          <Box>
            <CustomizedTypography
              variant="h4"
              fontWeight="500"
              gutterBottoms={16}
              color="textPrimary"
            >
              납품 정보
            </CustomizedTypography>

            <Grid container direction="column" className={classes.grid}>
              <Grid className="item" item xs={12}>
                <CustomizedTypography
                  className="title"
                  variant="body1"
                  color="textSecondary"
                  fontWeight="normal"
                >
                  납품 가능일
                </CustomizedTypography>
                <CustomizedTypography
                  variant="body1"
                  color="primary"
                  fontWeight="500"
                >
                  발주일로부터 {props?.dueDate}일
                </CustomizedTypography>
              </Grid>
            </Grid>
          </Box>
          <Divider className="divider" />
        </React.Fragment>
      )}
      <Box>
        <CustomizedTypography variant="h4" fontWeight="500" gutterBottoms={16}>
          발행 정보
        </CustomizedTypography>

        <Grid container direction="column" className={classes.grid}>
          <Grid className="item gutter-bottom-8" item xs={12}>
            <CustomizedTypography
              className="title"
              variant="body1"
              color="textSecondary"
              fontWeight="normal"
            >
              수신처
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
            >
              {props?.publish?.organizationName}
            </CustomizedTypography>
          </Grid>
          <Grid className="item gutter-bottom-8" item xs={12}>
            <CustomizedTypography
              className="title"
              variant="body1"
              color="textSecondary"
              fontWeight="normal"
            >
              프로젝트명
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
            >
              {props?.projectName}
            </CustomizedTypography>
          </Grid>
          <Grid className="item gutter-bottom-8" item xs={12}>
            <CustomizedTypography
              className="title"
              variant="body1"
              color="textSecondary"
              fontWeight="normal"
            >
              발행일시
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
            >
              {formatDate(
                props?.createdAt as string,
                'YYYY년 MM월 DD일 HH시 mm분'
              )}
            </CustomizedTypography>
          </Grid>
          {isNaN(Number(props?.expiration)) ||
          !props?.expiration ||
          Number(props.expiration) === 0 ? null : (
            <Grid className="item" item xs={12}>
              <CustomizedTypography
                className="title"
                variant="body1"
                color="textSecondary"
                fontWeight="normal"
              >
                유효기간
              </CustomizedTypography>
              <CustomizedTypography
                variant="body1"
                color="textPrimary"
                fontWeight="normal"
              >
                발행일로부터 {props?.expiration}일
              </CustomizedTypography>
            </Grid>
          )}
        </Grid>
      </Box>

      <Divider className="divider" />

      <Box>
        <CustomizedTypography
          variant="h4"
          fontWeight="500"
          gutterBottoms={16}
          color="textPrimary"
        >
          공급자 정보
        </CustomizedTypography>

        <Grid container direction="column" className={classes.grid}>
          <Grid className="item gutter-bottom-8" item xs={12}>
            <CustomizedTypography
              className="title"
              variant="body1"
              color="textSecondary"
              fontWeight="normal"
            >
              상호
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
            >
              {props?.supply?.organizationName}
            </CustomizedTypography>
          </Grid>
          <Grid className="item gutter-bottom-8" item xs={12}>
            <CustomizedTypography
              className="title"
              variant="body1"
              color="textSecondary"
              fontWeight="normal"
            >
              등록번호
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
            >
              {props?.supply?.companyRegistrationNumber}
            </CustomizedTypography>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default ReceivedQuotationNormalForm;

export const useStyles = makeStyles((theme) => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    '& .inner_title': {
      '& svg': {
        color: colorSet.gray900
      },
      '& .MuiIconButton-root': {
        padding: 6
      },
      '& > :first-child': {
        marginRight: 4
      }
    },
    '& .pre-line': {
      whiteSpace: 'pre-line'
    }
  },
  contents: {
    width: '100%',
    height: '100%',
    maxWidth: 600,
    margin: '0px auto',
    padding: '32px 16px',
    [theme.breakpoints.down('md')]: {
      padding: '32px 16px'
    }
  },
  divider: {
    margin: '35px 0px',
    backgroundColor: colorSet.gray000,
    [theme.breakpoints.down('md')]: {
      backgroundColor: colorSet.gray200,
      height: 6,
      position: 'relative',
      width: 'calc(100% + 32px)',
      right: 16,
      margin: '32px 0px',
      '&.inner': {
        width: '100%',
        backgroundColor: colorSet.gray900,
        margin: '16px 0px 14px',
        height: 1,
        right: 0
      }
    },
    '&.inner': {
      backgroundColor: colorSet.gray900,
      margin: '16px 0px 14px'
    }
  },

  tableRoot: {
    '& .file-table-subtotal': {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: 8
    }
  },
  tableHeader: {
    justifyContent: 'space-between',
    borderBottom: `1px solid ${colorSet.gray200}`,
    '& > div': {
      padding: '8px 0px'
    }
  },
  tableData: {
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    height: '100%',
    padding: '21px 0px',
    borderBottom: `1px solid ${colorSet.gray200}`,
    '& > :nth-child(1)': {
      display: 'flex',
      width: 'calc(100% - 100px)',
      '& .index': {
        width: 14
      },
      '& img': {
        marginLeft: 16
      },
      '& .thumbnail': {
        border: `1px solid ${colorSet.gray200}`,
        width: 70,
        height: 70,
        borderRadius: 8
      },
      '& .fileName': {
        maxWidth: 340,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'pre',
        [theme.breakpoints.down('md')]: {
          maxWidth: '100%'
        }
      },
      '& .material': {
        wordBreak: 'break-all',
        [theme.breakpoints.down('md')]: {
          maxWidth: '100%'
        }
      }
    },
    '& > :nth-child(2)': {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      flexDirection: 'column'
    }
  },
  grid: {
    '& .item': {
      display: 'flex',
      alignItems: 'center',
      '& .title': {
        minWidth: 85,
        marginRight: 8
      },
      '&.gutter-bottom-8': {
        marginBottom: 8
      }
    }
  },
  priceInfo: {
    '& .price-item': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '&.gutter-bottom-8': {
        marginBottom: 8
      }
    }
  },
  withdrawal: {
    display: 'flex',
    '& svg': {
      width: 20,
      height: 20,
      marginRight: 8,
      color: colorSet.errorMain
    }
  }
}));
